import { ControlValueAccessorValidatorBase } from '../control-value-accessor-validator-base';
import {
    Directive,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    QueryList,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import { BehaviorSubject, interval, Subject } from 'rxjs';
import { debounce } from 'rxjs/operators';
import { ValidationErrors } from '@angular/forms';

import { MatOptionSelectionChange } from '@angular/material/core';
import { FiDropdownItemDisabledFn, FiSelectSearchValidationFn } from '@fi-shared/fi-select-search/fi-select-search';
import { MatMenuItem } from '@angular/material/menu';
import { isDefined } from '@fi-shared/utils';

export type TItemWithChildren<T> = T & { ChildItems: TItemWithChildren<T>[] };

@Directive()
export abstract class SelectSearchBase<TItem, TRes>
    extends ControlValueAccessorValidatorBase<TRes>
    implements OnInit, OnChanges, OnDestroy
{
    abstract items: TItem[];
    @Input() displayValue: string;
    @Input() isSubItemsPaddingApplied: boolean = true;
    @Input() isChildItemsDisplayed: boolean;
    @Input() childItemsPropName: string;
    @Input() isDynamicItemHeight: boolean;

    // determine if we can select item in dropdown
    @Input() isOptionDisabledFn: FiDropdownItemDisabledFn<TItem> = () => false;

    // external validation function
    @Input() validationFn: FiSelectSearchValidationFn<TItem>;

    required = false;

    selectedItems$ = new BehaviorSubject<TItem[]>([]);

    filteredItems$ = new BehaviorSubject<TItem[]>([]);
    searchText: string;
    protected itemDepthPropertyName: string = 'Level';
    protected paddingUnits: string = 'px';
    protected listItemBasePadding: number = 20;
    protected listItemPaddingPerLevel: number = 10;

    protected searchTextChanged$ = new Subject<void>();

    constructor(protected injector: Injector) {
        super(injector);
    }

    ngOnInit(): void {
        /*this.selectedItems$.subscribe((v) => {  // todo looks like we don't need this
            this.updateFilteredItems();
        });*/
        this.updateFilteredItems();
        this.searchTextChanged$.pipe(debounce(() => interval(300))).subscribe(() => this.updateFilteredItems());
        this.buildHierarchy();
    }

    ngOnChanges(changes: SimpleChanges): void {
        let change = changes.ngModel;
        if (change && change.currentValue !== change.previousValue && !this.propagateChangesInProcess) {
            this.updateSelectedItemsFromModel();
            if (this.isConnectedItemsLayout) {
                this.expandSelectedItems();
            }
        }
        change = changes.items;
        if (change && change.currentValue !== change.previousValue && !change.firstChange) {
            this.updateSelectedItemsFromModel();
            this.updateFilteredItems();
            this.buildHierarchy();
        }
    }

    @Input() isConnectedItemsLayout: boolean;
    @Input() valuePropName: string;
    @Input() parentIdPropName: string;

    protected expandedItems$ = new BehaviorSubject<number[]>([]);
    protected setExpandableItems() {
        if (isDefined(this.parentIdPropName)) {
            const expandedMainItemIds = this.items
                .map((item: TItem) => item[this.parentIdPropName])
                .filter(
                    (item: number, index: number, self: number[]) => isDefined(item) && self.indexOf(item) === index,
                );
            this.expandedItems$.next(expandedMainItemIds);
        }
    }

    protected expandSelectedItems() {
        if (!isDefined(this.parentIdPropName) || !Array.isArray(this.ngModel) || !this.ngModel.length) {
            return;
        }

        const valueProp = this.valuePropName || 'Id';
        const selectedValues = new Set(
            (this.ngModel as Array<number | TItem>).map((selected) => {
                if (typeof selected === 'object') {
                    return selected[valueProp];
                }
                return selected;
            }),
        );
        const itemsByValue = new Map(this.items.map((item) => [item[valueProp], item]));
        const expandedIds = new Set(this.expandedItems$.value);

        selectedValues.forEach((selectedValue) => {
            let current = itemsByValue.get(selectedValue);
            while (isDefined(current?.[this.parentIdPropName])) {
                const parentId = current[this.parentIdPropName];
                expandedIds.add(parentId);
                current = itemsByValue.get(parentId);
            }
        });

        this.expandedItems$.next([...expandedIds]);
    }

    protected itemGroups$ = new BehaviorSubject<TItemWithChildren<TItem>[]>([]);
    protected buildHierarchy() {
        if (!this.isConnectedItemsLayout) return;

        const itemMap = new Map<number, TItemWithChildren<TItem>>();

        this.items.forEach((item: TItem) => {
            itemMap.set(item[this.valuePropName], { ...item, ChildItems: [] });
        });

        const output: TItemWithChildren<TItem>[] = [];

        this.items.forEach((item) => {
            if (item[this.parentIdPropName]) {
                const parent = itemMap.get(item[this.parentIdPropName]);
                if (parent) {
                    parent.ChildItems!.push(itemMap.get(item[this.valuePropName])!);
                }
            } else {
                output.push(itemMap.get(item[this.valuePropName])!);
            }
        });

        this.itemGroups$.next(output);
        this.updateFilteredItems();
    }

    deepChildrenSearch<T>(
        items: TItemWithChildren<T>[],
        searchFn: (item: TItemWithChildren<T>) => boolean,
    ): TItemWithChildren<T>[] {
        const searchRecursive = (item: TItemWithChildren<T>): TItemWithChildren<T> | null => {
            const matches: boolean = searchFn(item);

            const matchingChildren: TItemWithChildren<T>[] = item.ChildItems.map((child) =>
                searchRecursive(child),
            ).filter(Boolean);

            if (matches || matchingChildren.length > 0) {
                return {
                    ...item,
                    ChildItems: matchingChildren,
                };
            }

            return null;
        };

        return items.map((item: TItemWithChildren<T>) => searchRecursive(item)).filter(Boolean);
    }

    itemSelectingInProcess = false;
    protected onItemSelected(item: TItem, $event: MatOptionSelectionChange) {
        if ($event && !$event.isUserInput) return;
        this.itemSelectingInProcess = true;
        setTimeout(() => (this.itemSelectingInProcess = false));
    }

    removingItemInProcess = false;
    protected onItemRemoved(itemToRemove: TItem) {
        this.removingItemInProcess = true;
        setTimeout(() => (this.removingItemInProcess = false));
    }

    private lastSearchText: string;
    onSearchTextChanged() {
        if (!this.itemSelectingInProcess) {
            this.lastSearchText = this.searchText;
            this.searchTextChanged$.next();
        } else {
            // input has been cleared after selecting item from dropdown, we should restore it
            this.searchText = this.lastSearchText;
        }
    }

    protected abstract updateFilteredItems();

    protected updateSelectedItemsFromModel() {
        const selectedItems = this.getSelectedItemsFromModel();
        this.selectedItems$.next(selectedItems);
    }

    private propagateChangesInProcess = false;
    doPropagateChanges() {
        this.propagateChangesInProcess = true;
        setTimeout(() => (this.propagateChangesInProcess = false));
        super.doPropagateChanges();
    }

    protected abstract getSelectedItemsFromModel(): TItem[];

    doValidate(): ValidationErrors {
        if (isDefined(this.validationFn)) {
            const externalValidationResult = this.validationFn(this.selectedItems$.value);
            return externalValidationResult;
        }
        return {
            required: this.required && !this.selectedItems$.value.length,
        };
    }

    getPadding(item: TItem): string | number {
        if (item.hasOwnProperty(this.itemDepthPropertyName) && this.isSubItemsPaddingApplied) {
            return (
                this.listItemBasePadding +
                item[this.itemDepthPropertyName] * this.listItemPaddingPerLevel +
                this.paddingUnits
            );
        }
    }

    readonly itemSize: number = 50;
    readonly maxItemsToShow: number = 8;
    readonly minBufferPx: number = 576; // maxItemsToShow * 2 * itemSize
    readonly maxBufferPx: number = 864; // maxItemsToShow * 3 * itemSize
    private readonly displayedItemsAmountStep: number = 20;
    private displayedItemsAmount: number = this.displayedItemsAmountStep;

    protected preventMenuItemsViewportHeightUpdate: boolean;
    viewportHeight$ = new BehaviorSubject<number>(0);
    private _matMenuItems: HTMLElement[];
    @ViewChildren(MatMenuItem) set matMenuItems(value: QueryList<MatMenuItem>) {
        this._matMenuItems = value.map((matMenuItem: MatMenuItem) => matMenuItem._getHostElement());
        if (this.preventMenuItemsViewportHeightUpdate) return;
        setTimeout(() => this.updateViewportHeight());
    }

    private updateViewportHeight() {
        let itemsLength: number = this.filteredItems$.value.length;
        const maxItemsHeight: number = this.maxItemsToShow * this.itemSize;
        if (this.isChildItemsDisplayed) {
            const itemsChildItemsLength: number[] = this.filteredItems$.value.map((item) => item['ChildItems'].length);
            itemsChildItemsLength.forEach((childItemsLength) => (itemsLength = itemsLength + childItemsLength));
        }

        if (this.isDynamicItemHeight) {
            const elementsHeight: number = this._matMenuItems.reduce(
                (total, element) => total + element.offsetHeight,
                2,
            );

            this.viewportHeight$.next(Math.min(maxItemsHeight, elementsHeight));
            return;
        } else {
            const viewportHeight: number =
                itemsLength < this.maxItemsToShow ? itemsLength * this.itemSize : maxItemsHeight;
            this.viewportHeight$.next(viewportHeight);
        }
    }

    increaseDisplayedItemsAmount() {
        this.displayedItemsAmount += this.displayedItemsAmountStep;
    }

    getTrimmedFilteredItems(): TItem[] {
        return this.filteredItems$.value.slice(0, this.displayedItemsAmount);
    }

    isOptionDisabled(item: TItem): boolean {
        return this.isOptionDisabledFn(item, this.items);
    }

    protected ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
